import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Calendar, Bot, User, RefreshCw, Zap, Clock, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

const AdminTripDates = () => {
  const [tripDates, setTripDates] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [autoStatus, setAutoStatus] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [runningManualJob, setRunningManualJob] = useState(false);
  const [generateParams, setGenerateParams] = useState({
    month: new Date().getMonth() + 2 > 12 ? 1 : new Date().getMonth() + 2,
    year: new Date().getMonth() + 2 > 12 ? new Date().getFullYear() + 1 : new Date().getFullYear(),
    num_treks: 4,
    num_circuits: 2
  });
  const [formData, setFormData] = useState({
    circuit_id: "",
    start_date: "",
    end_date: "",
    available_spots: 10,
    price_override: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
    fetchAutoStatus();
    fetchSchedulerStatus();
  }, []);

  const fetchSchedulerStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/scheduler/status`, getAuthHeaders());
      setSchedulerStatus(res.data);
    } catch (error) {
      console.error("Error fetching scheduler status", error);
    }
  };

  const handleRunManualJob = async () => {
    setRunningManualJob(true);
    try {
      await axios.post(`${API}/admin/scheduler/run-now`, {}, getAuthHeaders());
      toast.success("Génération automatique exécutée avec succès !");
      fetchData();
      fetchAutoStatus();
    } catch (error) {
      toast.error("Erreur lors de l'exécution du job");
    } finally {
      setRunningManualJob(false);
    }
  };

  const fetchData = async () => {
    try {
      const [datesRes, circuitsRes] = await Promise.all([
        axios.get(`${API}/admin/trip-dates`, getAuthHeaders()),
        axios.get(`${API}/admin/circuits`, getAuthHeaders()),
      ]);
      setTripDates(datesRes.data);
      setCircuits(circuitsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/trip-dates/auto-status`, getAuthHeaders());
      setAutoStatus(res.data);
    } catch (error) {
      console.error("Error fetching auto status", error);
    }
  };

  const getCircuitName = (circuitId) => {
    const circuit = circuits.find(c => c.id === circuitId);
    return circuit?.title?.fr || "Circuit inconnu";
  };

  const getCircuitType = (circuitId) => {
    const circuit = circuits.find(c => c.id === circuitId);
    return circuit?.type || "unknown";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      price_override: formData.price_override ? parseFloat(formData.price_override) : null,
      source: "manual"
    };
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/trip-dates/${editingItem.id}`, data, getAuthHeaders());
        toast.success("Date mise à jour");
      } else {
        await axios.post(`${API}/admin/trip-dates`, data, getAuthHeaders());
        toast.success("Date créée");
      }
      setDialogOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      circuit_id: item.circuit_id,
      start_date: item.start_date,
      end_date: item.end_date,
      available_spots: item.available_spots,
      price_override: item.price_override || "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/trip-dates/${id}`, getAuthHeaders());
      toast.success("Date supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/trip-dates/${id}`, { status }, getAuthHeaders());
      toast.success(`Statut mis à jour: ${status === 'cancelled' ? 'Annulé' : status === 'closed' ? 'Clôturé' : status}`);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleGenerateAuto = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/admin/trip-dates/generate`, generateParams, getAuthHeaders());
      const result = res.data;
      
      if (result.summary.total_created > 0) {
        toast.success(`${result.summary.total_created} dates générées (${result.summary.treks} treks, ${result.summary.circuits_4x4} circuits)`);
      } else if (result.summary.skipped > 0) {
        toast.info(`${result.summary.skipped} dates ignorées (déjà existantes)`);
      } else {
        toast.warning("Aucune date générée. Vérifiez que vous avez des circuits actifs.");
      }
      
      setGenerateDialogOpen(false);
      fetchData();
      fetchAutoStatus();
    } catch (error) {
      toast.error("Erreur lors de la génération automatique");
    } finally {
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      circuit_id: "",
      start_date: "",
      end_date: "",
      available_spots: 10,
      price_override: "",
      notes: "",
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      open: "bg-green-100 text-green-800 border-green-200",
      closed: "bg-yellow-100 text-yellow-800 border-yellow-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
      completed: "bg-blue-100 text-blue-800 border-blue-200",
    };
    const labels = {
      open: "Ouvert",
      closed: "Clôturé",
      cancelled: "Annulé",
      completed: "Terminé",
    };
    return <Badge variant="outline" className={colors[status] || "bg-gray-100"}>{labels[status] || status || "Ouvert"}</Badge>;
  };

  const getSourceBadge = (source) => {
    if (source === "automatic") {
      return (
        <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200 gap-1">
          <Bot className="h-3 w-3" />
          Auto
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 gap-1">
        <User className="h-3 w-3" />
        Manuel
      </Badge>
    );
  };

  const getTypeBadge = (circuitId) => {
    const type = getCircuitType(circuitId);
    if (type === "trekking") {
      return <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">Trek</Badge>;
    }
    return <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200">4x4</Badge>;
  };

  const monthNames = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  // Group dates by status for stats
  const stats = {
    total: tripDates.length,
    open: tripDates.filter(d => d.status === "open" || !d.status).length,
    automatic: tripDates.filter(d => d.source === "automatic").length,
    manual: tripDates.filter(d => d.source !== "automatic").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-trip-dates">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold">Dates de voyage</h1>
          <p className="text-muted-foreground">Planifiez les départs de vos circuits et treks</p>
        </div>
        <div className="flex gap-2">
          {/* Generate Auto Dialog */}
          <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="generate-auto-btn">
                <Zap className="h-4 w-4" /> Génération auto
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-purple-600" />
                  Génération automatique
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Génère automatiquement des dates de départ pour le mois sélectionné.
                  Les dates existantes ne seront pas dupliquées.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mois cible</Label>
                    <Select 
                      value={generateParams.month.toString()} 
                      onValueChange={(v) => setGenerateParams({...generateParams, month: parseInt(v)})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {monthNames.map((name, idx) => (
                          <SelectItem key={idx} value={(idx + 1).toString()}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Année</Label>
                    <Select 
                      value={generateParams.year.toString()} 
                      onValueChange={(v) => setGenerateParams({...generateParams, year: parseInt(v)})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2025, 2026, 2027].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre de Treks</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="10"
                      value={generateParams.num_treks}
                      onChange={(e) => setGenerateParams({...generateParams, num_treks: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Nombre de Circuits 4x4</Label>
                    <Input 
                      type="number" 
                      min="0" 
                      max="10"
                      value={generateParams.num_circuits}
                      onChange={(e) => setGenerateParams({...generateParams, num_circuits: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>

                {autoStatus && (
                  <div className="bg-muted p-3 rounded-lg text-sm">
                    <p className="font-medium mb-1">État actuel:</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• {monthNames[autoStatus.current_month.month - 1]} {autoStatus.current_month.year}: {autoStatus.current_month.automatic_dates} dates auto</li>
                      <li>• {monthNames[autoStatus.next_month.month - 1]} {autoStatus.next_month.year}: {autoStatus.next_month.automatic_dates} dates auto</li>
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>Annuler</Button>
                  <Button onClick={handleGenerateAuto} disabled={generating} className="gap-2">
                    {generating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                    {generating ? "Génération..." : "Générer"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual Add Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-trip-date-btn">
                <Plus className="h-4 w-4" /> Ajouter manuellement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingItem ? "Modifier la date" : "Nouvelle date de départ"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Circuit</Label>
                  <Select value={formData.circuit_id} onValueChange={(v) => setFormData({ ...formData, circuit_id: v })} required>
                    <SelectTrigger><SelectValue placeholder="Sélectionner un circuit..." /></SelectTrigger>
                    <SelectContent>
                      {circuits.filter(c => c.is_active).map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className={c.type === "trekking" ? "bg-orange-100 text-orange-800" : "bg-emerald-100 text-emerald-800"}>
                              {c.type === "trekking" ? "Trek" : "4x4"}
                            </Badge>
                            {c.title.fr}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Places disponibles</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.available_spots}
                      onChange={(e) => setFormData({ ...formData, available_spots: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Prix spécial (€, optionnel)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.price_override}
                      onChange={(e) => setFormData({ ...formData, price_override: e.target.value })}
                      placeholder="Prix par défaut du circuit"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notes internes..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                  <Button type="submit">{editingItem ? "Mettre à jour" : "Créer"}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Scheduler Status Card */}
      {schedulerStatus && (
        <Card className="mb-6 border-purple-200 bg-purple-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                <CardTitle className="text-lg">Cron Automatique</CardTitle>
                <Badge variant="outline" className={schedulerStatus.running ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800"}>
                  {schedulerStatus.running ? "Actif" : "Inactif"}
                </Badge>
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleRunManualJob}
                disabled={runningManualJob}
                className="gap-2"
              >
                {runningManualJob ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Exécuter maintenant
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              {schedulerStatus.jobs.map(job => (
                <div key={job.id} className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-purple-500" />
                  <span className="font-medium">{job.name}</span>
                  <span className="text-xs">—</span>
                  <span>Prochain: <strong>{job.next_run_time ? new Date(job.next_run_time).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : 'Non planifié'}</strong></span>
                  <span className="text-xs text-muted-foreground">({job.trigger})</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total</CardDescription>
            <CardTitle className="text-2xl">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Ouverts</CardDescription>
            <CardTitle className="text-2xl text-green-600">{stats.open}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1"><Bot className="h-3 w-3" /> Automatiques</CardDescription>
            <CardTitle className="text-2xl text-purple-600">{stats.automatic}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1"><User className="h-3 w-3" /> Manuels</CardDescription>
            <CardTitle className="text-2xl text-blue-600">{stats.manual}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Circuit</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Dates</TableHead>
              <TableHead>Places</TableHead>
              <TableHead>Prix</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tripDates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Aucune date planifiée. Utilisez "Génération auto" pour créer des dates automatiquement.
                </TableCell>
              </TableRow>
            ) : (
              tripDates.map((item) => (
                <TableRow key={item.id} className={item.status === "cancelled" ? "opacity-50" : ""}>
                  <TableCell className="font-medium">{getCircuitName(item.circuit_id)}</TableCell>
                  <TableCell>{getTypeBadge(item.circuit_id)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{item.start_date} → {item.end_date}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={item.booked_spots >= item.available_spots ? "text-red-600 font-medium" : ""}>
                      {item.booked_spots || 0}/{item.available_spots}
                    </span>
                  </TableCell>
                  <TableCell>
                    {item.price_override ? `€${item.price_override}` : <span className="text-muted-foreground">Standard</span>}
                  </TableCell>
                  <TableCell>{getSourceBadge(item.source)}</TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} title="Modifier">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      
                      {(item.status === "open" || !item.status) && (
                        <>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-yellow-600" title="Clôturer">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Clôturer cette date ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Les nouvelles réservations seront bloquées. Cette action peut être annulée.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Non</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleUpdateStatus(item.id, "closed")} className="bg-yellow-600 hover:bg-yellow-700">
                                  Clôturer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-red-600" title="Annuler">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Annuler cette date ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Le départ sera marqué comme annulé. Les clients ayant réservé devront être contactés.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Non</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleUpdateStatus(item.id, "cancelled")} className="bg-red-600 hover:bg-red-700">
                                  Annuler le départ
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}

                      {item.status === "closed" && (
                        <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleUpdateStatus(item.id, "open")} title="Réouvrir">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                          </svg>
                        </Button>
                      )}

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-destructive" title="Supprimer">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer cette date ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. La date sera définitivement supprimée.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive hover:bg-destructive/90">
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminTripDates;
